import { CommonModule, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
  effect,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { StatusModel } from '@core/models/status.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import * as LayoutActions from '@core/redux/layout/layout.actions';
import * as StatusAction from '@core/redux/status/status.actions';
import * as TaskActions from '@core/redux/task/task.actions';
import { StoreTaskService } from '@core/services/store-task.service';
import { select, Store } from '@ngrx/store';
import { TaskPlusComponent } from '@ux/components/task-plus/task-plus.component';

import { BoardCardComponent } from '../board-card/board-card.component';
import { BoardColumnMenuComponent } from '../board-column-menu/board-column-menu.component';

declare let jQuery: any;

interface BoardColumnState {
  items: TaskModel[];
  isShowMenu: boolean;
  isLoading: boolean;
  allowUpdate: boolean;
  isAdmin: boolean;
}

@Component({
  selector: 'app-board-column',
  templateUrl: './board-column.component.html',
  styleUrls: ['./board-column.component.less'],
  standalone: true,
  imports: [FormsModule, NgStyle, CommonModule, BoardCardComponent, TaskPlusComponent, BoardColumnMenuComponent],
  providers: [StoreTaskService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoardColumnComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly taskService = inject(StoreTaskService);
  private readonly elementRef = inject(ElementRef);
  private readonly route = inject(ActivatedRoute);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly router = inject(Router);

  @Input({ required: true }) model!: StatusModel;
  @Input({ required: true }) statusId!: string;

  // State signals
  private readonly state = signal<BoardColumnState>({
    items: [],
    isShowMenu: false,
    isLoading: false,
    allowUpdate: true,
    isAdmin: false,
  });

  // Computed values
  readonly items = computed(() => this.state().items);
  readonly isShowMenu = computed(() => this.state().isShowMenu);
  readonly isLoading = computed(() => this.state().isLoading);
  readonly isAdmin = computed(() => this.state().isAdmin);

  // Observables
  readonly taskLoading$ = this.store.pipe(select(fromRoot.getTaskLoading));
  readonly statusLoading$ = this.store.pipe(select(fromRoot.getStatusLoading));
  readonly taskLoadingOrder$ = this.store.pipe(select(fromRoot.getTaskLoadingOrder));

  // Model copy for editing
  modelCopy: StatusModel;

  constructor() {
    this.modelCopy = new StatusModel();

    // Setup loading effect
    effect(() => {
      jQuery('.board-column__task-plus').css({
        display: this.state().isLoading ? 'none' : 'flex',
      });
    });

    // Setup subscriptions
    this.statusLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loading => {
      this.updateState({ isLoading: loading });
    });

    this.store.pipe(select(fromRoot.getUserCanAdmin), takeUntilDestroyed(this.destroyRef)).subscribe(isAdmin => {
      this.updateState({ isAdmin: isAdmin || false });
    });
  }

  ngOnInit(): void {
    this.setupTaskList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model'] && changes['model'].currentValue) {
      this._copyModel();
    }
  }

  ngAfterViewInit(): void {
    this.setupSortable();
  }

  private setupTaskList(): void {
    this.taskService
      .getTaskListByStatusId(this.statusId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        if (this.state().allowUpdate) {
          const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
          this.updateState({ items: sortedItems });
          this.cd.detectChanges();
        }
      });
  }

  private setupSortable(): void {
    jQuery(this.elementRef.nativeElement.querySelector('.board-column__cards'))
      .sortable({
        connectWith: '.board-column__cards',
        items: '.board-column__card',
        placeholder: 'card-placeholder',
        dropOnEmpty: true,
        forcePlaceholderSize: true,
        tolerance: 'pointer',
        start: () => {
          jQuery('.board-column__task-plus').css({ display: 'none' });
        },
        stop: () => {
          setTimeout(() => jQuery('.board-column__task-plus').css({ display: 'flex' }), 300);
        },
        update: (event: Event, ui: any) => this.handleSortableUpdate(ui),
      })
      .disableSelection();
  }

  private handleSortableUpdate(ui: any): void {
    const model = new TaskModel();
    model.setAttributes({
      id: ui.item.attr('taskid'),
      beforeId: ui.item.next().attr('taskid'),
      afterId: ui.item.prev().attr('taskid'),
      statusId: ui.item.parents('.board__column').attr('statusid'),
    });
    this.store.dispatch(new TaskActions.OrderAction(model));
  }

  private updateState(patch: Partial<BoardColumnState>): void {
    this.state.update(state => ({ ...state, ...patch }));
  }

  onSaveStatus(): void {
    if (this.model.name !== this.modelCopy.name) {
      const updatedModel = new StatusModel();
      updatedModel.setAttributes({
        ...this.model,
        name: this.modelCopy.name,
      });
      this.store.dispatch(new StatusAction.SaveAction(updatedModel));
    }
  }

  onEnterInputStatusName(): void {
    if (this.modelCopy.name !== '') {
      this.onSaveStatus();
    }
  }

  onBlurInputStatusName(): void {
    if (this.modelCopy.name === '') {
      this._copyModel();
    } else {
      this.onSaveStatus();
    }
  }

  onShowTaskForm(index: number, afterId = null, beforeId = null): void {
    if (this.state().items.find(task => task.id === 'newTask')) {
      return;
    }

    this.updateState({ allowUpdate: false });

    const model = new TaskModel();
    model.setAttributes({
      id: 'newTask',
      name: '',
      insertIndex: index,
      statusId: this.statusId,
      afterId,
      beforeId,
    });

    const newItems = [...this.state().items];
    newItems.splice(index, 0, model);
    this.updateState({ items: newItems });
  }

  onToggleBackLog(): void {
    this.store.dispatch(new LayoutActions.ToggleBackLog());
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.updateState({ isShowMenu: !this.state().isShowMenu });
  }

  close(): void {
    this.updateState({ isShowMenu: false });
  }

  openTask(event: Event, id: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (id !== 'newTask') {
      this.router.navigate([{ outlets: { task: ['view', id] } }], {
        relativeTo: this.route.parent,
        queryParamsHandling: 'merge',
      });
    }
  }

  changeAllowUpdateState(isAllowUpdate: boolean): void {
    this.updateState({ allowUpdate: isAllowUpdate });
  }

  protected _copyModel(): void {
    if (this.model) {
      this.modelCopy = Object.assign({}, this.model);
    } else {
      const emptyModel = new StatusModel();
      emptyModel.setAttributes({ name: '' });
      this.modelCopy = emptyModel;
    }
  }

  ngOnDestroy(): void {
    // Cleanup is handled automatically by takeUntilDestroyed
  }
}
